import { useMemo, type ReactElement, type ReactNode } from 'react';

import { Tooltip, Typography } from '@scaleaq/scaleui';
import { useTranslation } from 'react-i18next';

import { EllipsisFormFieldWithCopyToClipboard } from '@/components/EllipsisFormFieldWithCopyToClipboard.tsx';
import { colors } from '@/utils';

import { StyledCustomValue, StyledValueText } from './infoGroupCard.styles.ts';
import { InfoGroupFieldProps } from './infoGroupCard.types.ts';

// regex to match non-printable control characters (ASCII 0-31 and 127)
// oxlint-disable-next-line no-control-regex
const controlCharsRegex = /[\u0000-\u001F\u007F]/g;

/** Muted label above a bold value; trims and strips control characters. */
export const InfoGroupField = ({
  label,
  value,
  valueContent,
  disabled = false,
  tooltip,
  unit,
  withSpaceToUnit = false,
  copyable = false,
}: InfoGroupFieldProps) => {
  const { t } = useTranslation();

  const displayText = useMemo(() => {
    let trimmedValue: string | undefined;
    if (value === undefined || value === null) {
      trimmedValue = undefined;
    } else {
      const cleaned = value.toString().replace(controlCharsRegex, '').trim();
      trimmedValue = cleaned.length > 0 ? cleaned : undefined;
    }
    const valueString = trimmedValue ? trimmedValue : t('notApplicable');
    const space = trimmedValue && withSpaceToUnit ? ' ' : '';
    const unitOrEmpty =
      trimmedValue && trimmedValue.length > 0 && valueString && unit ? `${unit}` : '';
    return `${valueString}${space}${unitOrEmpty}`;
  }, [value, t, unit, withSpaceToUnit]);

  return (
    <div>
      <Typography
        color={colors.text.neutral.on.soft['200']}
        disabled={disabled}
        style={{ whiteSpace: 'nowrap' }}
        type='body_small'
      >
        {label}
      </Typography>

      <Tooltip tooltip={tooltip} placement='top'>
        {_renderValue({ copyable, disabled, displayText, label, tooltip, valueContent })}
      </Tooltip>
    </div>
  );
};

type RenderValueProps = {
  copyable: boolean;
  disabled: boolean;
  displayText: string;
  label: string;
  tooltip?: string | ReactNode;
  valueContent: ReactNode;
};

const _renderValue = ({
  copyable,
  disabled,
  displayText,
  label,
  valueContent,
  tooltip,
}: RenderValueProps): ReactElement => {
  if (valueContent != null) {
    return <StyledCustomValue>{valueContent}</StyledCustomValue>;
  }
  if (copyable) {
    return (
      <EllipsisFormFieldWithCopyToClipboard
        tooltip={tooltip}
        value={displayText}
        valueName={label}
      />
    );
  }
  return (
    <StyledValueText disabled={disabled} type='body_large' weight={700}>
      {displayText}
    </StyledValueText>
  );
};
